import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';

/**
 * Tablero del ciclo (GET /kpi/ciclo): 5 columnas por etapa con conteos +
 * tarjetas. Alimenta la bandeja del Home (Fase 4) y el tablero (Fase 5).
 *
 * @param {object} [params]
 * @param {number} [params.idClient]           Acota el tablero a un cliente.
 * @param {string[]|string} [params.severidad] Severidad(es) de hallazgo. Array o CSV.
 * @param {number} [params.limit]              Máx. tarjetas por columna (el count es el total real).
 * @returns {{ columnas, loading, error, refetch }}
 */
export const useCicloTablero = ({ idClient, severidad, limit, sort } = {}) => {
  const [columnas, setColumnas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const sevKey = Array.isArray(severidad) ? severidad.join(',') : severidad;

  const fetchTablero = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const token = getAccessTokenApi();
      const params = {};
      if (idClient) params.idClient = idClient;
      if (sevKey) params.severidad = sevKey;
      if (limit) params.limit = limit;
      if (sort) params.sort = sort;

      const { data } = await axios.get(`${API_PATH}/kpi/ciclo`, {
        params,
        headers: { Authorization: token },
        signal: controller.signal,
      });
      setColumnas(data.columnas ?? []);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      setError(err);
      setColumnas([]);
    } finally {
      setLoading(false);
    }
  }, [idClient, sevKey, limit, sort]);

  useEffect(() => {
    fetchTablero();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchTablero]);

  return { columnas, loading, error, refetch: fetchTablero };
};

/**
 * Cola paginada de UNA etapa del ciclo (GET /kpi/ciclo?etapa=...). Alimenta la
 * cola accionable de CicloPage. Devuelve { etapa, count, monto, rows, limit, offset }.
 */
export const useCicloEtapa = ({ etapa, idClient, severidad, limit = 25, offset = 0 } = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const sevKey = Array.isArray(severidad) ? severidad.join(',') : severidad;

  const fetchEtapa = useCallback(async () => {
    if (!etapa) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const token = getAccessTokenApi();
      const params = { etapa, limit, offset };
      if (idClient) params.idClient = idClient;
      if (sevKey) params.severidad = sevKey;
      const { data: resp } = await axios.get(`${API_PATH}/kpi/ciclo`, {
        params,
        headers: { Authorization: token },
        signal: controller.signal,
      });
      setData(resp);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [etapa, idClient, sevKey, limit, offset]);

  useEffect(() => {
    fetchEtapa();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchEtapa]);

  return { data, loading, error, refetch: fetchEtapa };
};
